import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
// @mui
import { 
  Box, 
  Step, 
  Stepper, 
  StepLabel, 
  StepContent,
  Typography, 
  Button, 
  Stack, 
  Divider, 
  Grid,
  Card
} from '@mui/material';
import { styled } from '@mui/material/styles';
// components
import Iconify from '../Iconify';
// routes
import { PATH_DASHBOARD } from '../../routes/paths';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(5),
  },
}));

// ----------------------------------------------------------------------

HelpCenter3DConnectionDialog.propTypes = {
  onClose: PropTypes.func,
  initialStep: PropTypes.number,
};

export default function HelpCenter3DConnectionDialog({ onClose, initialStep = 0 }) {
  const [activeStep, setActiveStep] = useState(initialStep);

  useEffect(() => {
    setActiveStep(initialStep);
  }, [initialStep]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const steps = [
    {
      label: 'Activate Your 3D Model',
      description: 'Ensure your 3D model is activated in the Model Library. An active model can be connected to products.',
      icon: 'carbon:switch',
      content: (
        <Box sx={{ mt: 2 }}>
          <Card sx={{ p: 2, mb: 2, bgcolor: 'background.neutral' }}>
            <Typography variant="body2" sx={{ color: 'text.primary', mb: 1 }}>
              <strong>How to activate:</strong>
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 0 }}>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                Go to your Model Library
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                Open the model details page
              </Typography>
              <Typography component="li" variant="body2">
                Click the "Activate Model" button in the top-right corner
              </Typography>
            </Box>
          </Card>
          <Stack direction="row" spacing={1}>
            <Button
              component={Link}
              to={PATH_DASHBOARD.modelLibrary.all}
              variant="outlined"
              size="small"
              startIcon={<Iconify icon="eva:grid-outline" />}
              onClick={onClose}
            >
              Go to Model Library
            </Button>
          </Stack>
        </Box>
      )
    },
    {
      label: 'Navigate to Product Catalog',
      description: 'Go to your product catalog where you can view and manage all your products.',
      icon: 'eva:shopping-bag-outline',
      content: (
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={3} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <Card sx={{ p: 3, bgcolor: 'background.neutral' }}>
                <Typography variant="body2" paragraph>
                  Your product catalog shows all products from your connected store. You can connect 3D models to individual products or multiple products at once.
                </Typography>
                <Box sx={{ 
                  height: 180, 
                  mb: 2,
                  mx: 'auto',
                  borderRadius: 1,
                  boxShadow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#f5f5f5',
                  border: '1px dashed',
                  borderColor: 'divider'
                }}>
                  <Iconify icon="eva:grid-fill" sx={{ width: 48, height: 48, color: 'primary.main', mb: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    Product Catalog View
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>
          <Stack direction="row" spacing={1}>
            <Button
              component={Link}
              to={PATH_DASHBOARD.ecommerce.library}
              variant="outlined"
              size="small"
              startIcon={<Iconify icon="eva:shopping-bag-outline" />}
              onClick={onClose}
            >
              Go to Product Catalog
            </Button>
          </Stack>
        </Box>
      )
    },
    {
      label: 'Connect Model to Products',
      description: 'Select products and connect your activated 3D model to them.',
      icon: 'eva:link-2-fill',
      content: (
        <Box sx={{ mt: 2 }}>
          <Card sx={{ p: 2, mb: 2, bgcolor: 'background.neutral' }}>
            <Typography variant="body2" sx={{ color: 'text.primary', mb: 1 }}>
              <strong>Two ways to connect:</strong>
            </Typography>
            <Box component="ol" sx={{ pl: 3, mb: 0 }}>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                <strong>From product catalog:</strong> Select products and click "Connect to 3D Model", then choose your model
              </Typography>
              <Typography component="li" variant="body2">
                <strong>From model details:</strong> Click "Connect to Products" and select which products to connect to this model
              </Typography>
            </Box>
          </Card>
          
          <Card sx={{ p: 2, mb: 2, bgcolor: 'background.primary', color: 'primary.contrastText' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Iconify icon="eva:bulb-outline" />
              <Typography variant="subtitle2">Pro Tip</Typography>
            </Stack>
            <Typography variant="body2">
              You can connect one model to multiple products, or connect multiple models to a single product 
              for different angle views or variant options.
            </Typography>
          </Card>
        </Box>
      )
    },
    {
      label: 'Verify Connection & Preview',
      description: 'Check that your model is properly connected and preview how it will appear to customers.',
      icon: 'eva:checkmark-circle-2-outline',
      content: (
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <Card sx={{ p: 3, mb: 2, bgcolor: 'background.neutral' }}>
                <Typography variant="body2" paragraph>
                  After connecting your model, you can verify it's working by:
                </Typography>
                <Box component="ul" sx={{ pl: 2 }}>
                  <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                    Checking the "Linked Products" section on your model details page
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                    Using the "View 3D Model" button to preview how it will appear
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                    Viewing product details to see connected 3D models
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>
          
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Congratulations! Your 3D model is now connected to your products.
          </Typography>
          
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              onClick={onClose}
              startIcon={<Iconify icon="eva:checkmark-circle-2-fill" />}
            >
              Got It
            </Button>
            
            <Button
              component={Link}
              to={PATH_DASHBOARD.modelLibrary.all}
              variant="outlined"
              onClick={onClose}
            >
              View My Models
            </Button>
          </Stack>
        </Box>
      )
    }
  ];

  return (
    <RootStyle>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>
          Connecting 3D Models to Products
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Follow these steps to connect your 3D models to products and create interactive shopping experiences
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((step, index) => (
          <Step key={step.label}>
            <StepLabel 
              StepIconComponent={() => (
                <Box 
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: index <= activeStep ? 'primary.main' : 'grey.400',
                    color: 'white'
                  }}
                >
                  <Iconify icon={step.icon} width={18} height={18} />
                </Box>
              )}
            >
              <Typography variant="subtitle1">{step.label}</Typography>
              <Typography variant="body2" color="text.secondary">{step.description}</Typography>
            </StepLabel>
            <StepContent>
              {step.content}
              <Box sx={{ mb: 2, mt: 3 }}>
                <div>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    {index === steps.length - 1 ? 'Finish' : 'Continue'}
                  </Button>
                  <Button
                    disabled={index === 0}
                    onClick={handleBack}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    Back
                  </Button>
                </div>
              </Box>
            </StepContent>
          </Step>
        ))}
      </Stepper>
    </RootStyle>
  );
} 